/**
 * Themed dialog, built on the shared overlay shell (`overlay-shell.js`) — a native `<dialog>`
 * shown with `showModal()`. The platform supplies focus trap, focus restore, and Escape; this
 * module owns content + Promise resolution semantics only, and joins the shell's
 * one-overlay-at-a-time latch (a confirm superseded by another overlay resolves its mode's
 * dismiss value — no live flow stacks a confirm over an open overlay today).
 *
 * Two modes:
 *
 * - **Confirm:** Cancel + Confirm buttons. Promise resolves `true` on confirm,
 *   `false` on cancel (Esc, outside click, Cancel). Cancel auto-focused.
 *   `destructive: true` styles the confirm button `.btn-danger` — the platform's
 *   register for a genuinely destructive confirm. Since no-quit-cheesing it has
 *   no caller (see below); it is kept as the dialog's danger vocabulary, not
 *   because any doc mandates it — DESIGN § 12 is Visual coherence and says
 *   nothing about confirms, so an earlier citation of it here was wrong.
 *
 * - **Acknowledge:** single button (when `acknowledgeLabel` is provided).
 *   Promise always resolves `true`. The button auto-focuses; Esc / outside
 *   click also dismiss as `true`. Used for one-off info popups (greetings).
 *
 * Title is optional — omit for body-only layouts where the message carries
 * itself.
 *
 * Confirm mode's live caller is the pause menu's Quit entry (game-menu.js) — not
 * destructive (no-quit-cheesing: the game stays live and Play resumes it exactly
 * where you left off, DESIGN.md § 13 "The game remembers you"). The confirm beat
 * survives anyway, deliberately: leaving the board is still worth one extra tap,
 * just no longer a warning — plain `.btn-primary`, no `destructive` flag. (It sat
 * caller-less between End-Run's removal 2026-06 and the menu landing — the mode
 * was kept on the principle, then earned its consumer.)
 */
import { div, btn } from "./utils.js";
import { t } from "./i18n.js";
import { openVellumOverlay } from "./overlay-shell.js";

// why: per-instance unique IDs so two open dialogs (e.g. async re-entry) don't
// collide on aria-labelledby / aria-describedby targets.
let dialogIdCounter = 0;

/**
 * @param {object} opts
 * @param {string} [opts.title] - dialog title (one short sentence). Omit for
 *   body-only layouts.
 * @param {string} [opts.body] - dialog body. `\n` splits into paragraphs.
 * @param {string} [opts.confirmLabel] - confirm button label (confirm mode)
 * @param {string} [opts.cancelLabel] - cancel button label (defaults to t('cancel'))
 * @param {boolean} [opts.destructive] - styles confirm button as .btn-danger
 * @param {string} [opts.acknowledgeLabel] - if present, switches to ack mode:
 *   single primary button, all dismiss paths resolve `true`.
 * @returns {Promise<boolean>}
 */
export function confirmDialog(opts) {
  return new Promise((resolve) => openDialog(opts, resolve));
}

function openDialog(opts, resolve) {
  const { actions, focusOnOpen, primaryBtn, secondaryBtn, dismissValue } =
    buildActions(opts);

  // Pre-initialized to the mode's dismiss value: the shell's Escape/outside-click/supersede
  // paths run entirely inside openVellumOverlay and call `dismiss()` with no argument, so they
  // resolve this closure variable by default rather than by parameter.
  let result = dismissValue;
  const { panel, dismiss } = openVellumOverlay("confirm-dialog-panel", {
    role: "alertdialog",
    // No corner × here: an alertdialog's Cancel IS its close, and a second dismiss affordance
    // beside it would blur which one the "no" answer is (the shell's `closeButton` paragraph).
    closeButton: false,
    onClose: () => resolve(result),
  });

  attachLabelledContent(panel, opts, ++dialogIdCounter);
  panel.appendChild(actions);

  primaryBtn.addEventListener("click", () => {
    result = true;
    dismiss();
  });
  secondaryBtn?.addEventListener("click", () => {
    result = false;
    dismiss();
  });

  focusOnOpen.focus();
}

/** Appends the optional title + body into `panel`, wiring aria-labelledby/-describedby as each is
 *  present — the per-instance `id` keeps two open dialogs' targets from colliding. */
function attachLabelledContent(panel, opts, id) {
  if (opts.title) {
    const titleId = `confirm-dialog-title-${id}`;
    panel.appendChild(buildTitle(opts.title, titleId));
    panel.setAttribute("aria-labelledby", titleId);
  }
  const bodyId = `confirm-dialog-body-${id}`;
  const bodyEl = buildBody(opts.body, bodyId);
  if (bodyEl) {
    panel.appendChild(bodyEl);
    panel.setAttribute("aria-describedby", bodyId);
  }
}

function buildTitle(title, id) {
  const el = document.createElement("h2");
  el.id = id;
  el.className = "confirm-dialog-title";
  el.textContent = title;
  return el;
}

function buildBody(body, id) {
  const paragraphs = (body ?? "").split("\n").filter(Boolean);
  if (paragraphs.length === 0) return null;
  const wrapper = div("confirm-dialog-body-group");
  wrapper.id = id;
  for (const paragraph of paragraphs) {
    const p = document.createElement("p");
    p.className = "confirm-dialog-body";
    p.textContent = paragraph;
    wrapper.appendChild(p);
  }
  return wrapper;
}

function buildActions({
  confirmLabel,
  cancelLabel,
  acknowledgeLabel,
  destructive,
}) {
  const actions = div("confirm-dialog-actions");
  if (acknowledgeLabel) {
    const ackBtn = btn(acknowledgeLabel, "btn-primary");
    actions.appendChild(ackBtn);
    return {
      actions,
      focusOnOpen: ackBtn,
      primaryBtn: ackBtn,
      secondaryBtn: null,
      dismissValue: true,
    };
  }
  const cancelBtn = btn(cancelLabel ?? t("cancel"), "btn-secondary");
  const confirmBtn = btn(
    confirmLabel,
    destructive ? "btn-danger" : "btn-primary",
  );
  actions.appendChild(cancelBtn);
  actions.appendChild(confirmBtn);
  return {
    actions,
    // why: focus the safe action so a stray Enter doesn't auto-destruct.
    focusOnOpen: cancelBtn,
    primaryBtn: confirmBtn,
    secondaryBtn: cancelBtn,
    dismissValue: false,
  };
}
